export const ADMIN_LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    nativeLabel: 'English',
    locale: 'en-US',
    dir: 'ltr',
  },
  {
    code: 'cs',
    label: 'Czech',
    nativeLabel: 'Čeština',
    locale: 'cs-CZ',
    dir: 'ltr',
  },
  {
    code: 'ru',
    label: 'Russian',
    nativeLabel: 'Русский',
    locale: 'ru-RU',
    dir: 'ltr',
  },
] as const;

export type AdminLanguageCode = (typeof ADMIN_LANGUAGES)[number]['code'];

export const ADMIN_DEFAULT_LANGUAGE: AdminLanguageCode = 'en';

export const adminEn = {
  'admin.common.access': 'Access',
  'admin.common.actions': 'Actions',
  'admin.common.active': 'Active',
  'admin.common.add': 'Add',
  'admin.common.all': 'All',
  'admin.common.approve': 'Approve',
  'admin.common.basic': 'Basic',
  'admin.common.cancel': 'Cancel',
  'admin.common.close': 'Close',
  'admin.common.configured': 'configured',
  'admin.common.create': 'Create',
  'admin.common.delete': 'Delete',
  'admin.common.destination': 'Destination',
  'admin.common.edit': 'Edit',
  'admin.common.exchange': 'Exchange',
  'admin.common.exchanges': 'Exchanges',
  'admin.common.inactive': 'Inactive',
  'admin.common.loading': 'Loading...',
  'admin.common.network': 'Network',
  'admin.common.noNote': 'No note',
  'admin.common.paused': 'Paused',
  'admin.common.pending': 'Pending',
  'admin.common.private': 'Private',
  'admin.common.rejected': 'Rejected',
  'admin.common.reject': 'Reject',
  'admin.common.save': 'Save',
  'admin.common.status': 'Status',
  'admin.common.total': '{{count}} total',
  'admin.common.uid': 'UID',
  'admin.common.update': 'Update',
  'admin.common.verified': 'Verified',

  'admin.language.current': 'Admin language',
  'admin.language.openSelector': 'Change admin language',
  'admin.language.title': 'Admin language',

  'admin.login.title': 'Admin Login',
  'admin.login.subtitle': 'Enter credentials to continue.',
  'admin.login.username': 'Username',
  'admin.login.password': 'Password',
  'admin.login.login': 'Login',
  'admin.login.loggingIn': 'Logging in...',
  'admin.login.invalidCredentials': 'Invalid credentials',

  'admin.page.eyebrow': 'Admin',
  'admin.page.title': 'Control Panel',
  'admin.page.metricUsers': 'Users',
  'admin.page.metricPending': 'Pending',
  'admin.page.metricActivePairs': 'Active pairs',
  'admin.page.tabPairs': 'Pairs',
  'admin.page.tabVerifications': 'Verifications',
  'admin.page.tabUsers': 'Users',
  'admin.page.tabAnnouncements': 'Announcements',
  'admin.page.tabOperations': 'Operations',
  'admin.page.tabSignals': 'Signals',
  'admin.page.tabBotPosts': 'Bot posts',

  'admin.time.label': 'Time',

  'admin.operations.title': 'Operations',
  'admin.operations.detail': 'Wallet API health and admin audit history.',
  'admin.operations.refresh': 'Refresh',
  'admin.operations.walletApis': 'Wallet APIs',
  'admin.operations.checkedAt': 'Checked: {{date}}',
  'admin.operations.healthLoadError': 'Failed to load wallet API health',
  'admin.operations.statusOk': 'OK',
  'admin.operations.statusIssue': 'Issue',
  'admin.operations.configured': 'Configured',
  'admin.operations.reachable': 'Reachable',
  'admin.operations.authorized': 'Authorized',
  'admin.operations.auditLog': 'Audit log',
  'admin.operations.auditLoadError': 'Failed to load audit log',
  'admin.operations.noAuditLogs': 'No audit records yet',
  'admin.operations.time': 'Time',
  'admin.operations.admin': 'Admin',
  'admin.operations.action': 'Action',
  'admin.operations.entity': 'Entity',
  'admin.operations.change': 'Change',

  'admin.pairs.title': 'Manage pairs',
  'admin.pairs.detail': '{{configured}} configured - {{active}} active - {{inactive}} inactive',
  'admin.pairs.addPair': 'Add pair',
  'admin.pairs.position': 'Position',
  'admin.pairs.coins': 'Coins',
  'admin.pairs.spread': 'Spread',
  'admin.pairs.safety': 'Profitability',
  'admin.pairs.note': 'Note',
  'admin.pairs.moveGroup': 'Move {{coin}}',
  'admin.pairs.moveUp': 'Move {{coin}} up',
  'admin.pairs.moveDown': 'Move {{coin}} down',
  'admin.pairs.editPair': 'Edit Pair',
  'admin.pairs.createPair': 'Create New Pair',
  'admin.pairs.pairType': 'Pair Type',
  'admin.pairs.singleCoin': 'Single Coin',
  'admin.pairs.doubleCoin': 'Double Coin',
  'admin.pairs.coinFrom': 'Coin From',
  'admin.pairs.coinTo': 'Coin To',
  'admin.pairs.exchangeBuy': 'Exchange Buy',
  'admin.pairs.exchangeSell': 'Exchange Sell',
  'admin.pairs.accessLevel': 'Access Level',
  'admin.pairs.basicAccess': 'Basic (All Users)',
  'admin.pairs.privateAccess': 'Private (Verified Only)',
  'admin.pairs.safetySignal': 'Profitability signal',
  'admin.pairs.safetyValue': 'Profitability {{percent}}%',
  'admin.pairs.safetyColor': 'Profitability color',
  'admin.pairs.safe': 'High',
  'admin.pairs.watch': 'Medium',
  'admin.pairs.risky': 'Low',
  'admin.pairs.safetyPercent': 'Profitability %',
  'admin.pairs.ribbonColor': 'Ribbon color',
  'admin.pairs.pairNote': 'Pair note',
  'admin.pairs.pairNoteHelp': 'Shown to users on the pair card.',
  'admin.pairs.alertMessage': 'Telegram alert message',
  'admin.pairs.alertMessageHelp': 'Optional. Supports {pair}, {route}, {spread}, {coin_from}, {coin_to}, {exchange_buy}, {exchange_sell}.',
  'admin.pairs.baseSpread': 'Base spread %',
  'admin.pairs.negativeSpread': 'Cooldown spread %',
  'admin.pairs.negativeSpreadShort': 'Cooldown',

  'admin.circles.title': 'Signal circles',
  'admin.circles.detail': '{{active}} active - {{total}} total',
  'admin.circles.loadError': 'Failed to load signal circles',
  'admin.circles.launchError': 'Failed to launch signal',
  'admin.circles.manualTab': 'Manual signals',
  'admin.circles.warmupTab': 'Warmup signals',
  'admin.circles.pair': 'Pair',
  'admin.circles.signalSpread': 'Signal spread %',
  'admin.circles.negativeSpread': 'Cooldown spread %',
  'admin.circles.cooldownHours': 'Cooldown hours',
  'admin.circles.expiresIn': 'Expires in hours',
  'admin.circles.notes': 'Notes',
  'admin.circles.noPairSelected': 'No pair selected',
  'admin.circles.launch': 'Launch signal',
  'admin.circles.signalSpreadShort': 'Signal',
  'admin.circles.cooldownShort': 'Cooldown',
  'admin.circles.notifications': 'Notify / tx',
  'admin.circles.started': 'Started',
  'admin.circles.expires': 'Expires',
  'admin.circles.stop': 'Stop',
  'admin.circles.empty': 'No signal circles yet',

  'admin.warmups.loadError': 'Failed to load warmup schedules',
  'admin.warmups.saveError': 'Failed to save warmup schedule',
  'admin.warmups.deleteConfirm': 'Delete warmup schedule "{{name}}"?',
  'admin.warmups.sourceLabel': 'Warmup',
  'admin.warmups.name': 'Schedule name',
  'admin.warmups.pairs': 'Pairs',
  'admin.warmups.selectedPairs': '{{count}} selected',
  'admin.warmups.noPairsSelected': 'No pairs selected',
  'admin.warmups.manualOnlyNotice': 'Each run picks one selected active pair at random; interval, length, and spread ranges are randomized too. Cron checks every 7 minutes, so a due warmup can launch up to about 7 minutes later. HIVE and STEEM pairs are manual-only and hidden.',
  'admin.warmups.intervalMin': 'Interval min, min',
  'admin.warmups.intervalMax': 'Interval max, min',
  'admin.warmups.durationMin': 'Length min, min',
  'admin.warmups.durationMax': 'Length max, min',
  'admin.warmups.spreadMin': 'Spread min %',
  'admin.warmups.spreadMax': 'Spread max %',
  'admin.warmups.negativeSpread': 'Cooldown spread %',
  'admin.warmups.cooldownHours': 'Cooldown hours',
  'admin.warmups.cancelEdit': 'Cancel edit',
  'admin.warmups.create': 'Create schedule',
  'admin.warmups.update': 'Update schedule',
  'admin.warmups.schedule': 'Schedule',
  'admin.warmups.nextRun': 'Next run',
  'admin.warmups.lastRun': 'Last run',
  'admin.warmups.runCount': 'Runs',
  'admin.warmups.empty': 'No warmup schedules yet',
  'admin.warmups.recentRuns': 'Recent warmup runs',
  'admin.warmups.rule': 'Rule',
  'admin.warmups.duration': 'Length',
  'admin.warmups.noRuns': 'No warmup runs yet',
  'admin.warmups.runNow': 'Run now',
  'admin.warmups.pause': 'Pause',
  'admin.warmups.resume': 'Resume',

  'admin.verifications.title': 'Verifications',
  'admin.verifications.detail': '{{count}} {{status}}',
  'admin.verifications.loadError': 'Failed to load verifications',
  'admin.verifications.empty': 'No {{status}} verifications',
  'admin.verifications.telegramId': 'Telegram ID: {{id}}',
  'admin.verifications.exchange': 'Exchange: {{exchange}}',
  'admin.verifications.uid': 'UID: {{uid}}',
  'admin.verifications.rejection': 'Rejection: {{reason}}',
  'admin.verifications.screenshot': 'Screenshot:',
  'admin.verifications.revealScreenshot': 'Reveal screenshot',
  'admin.verifications.loadingScreenshot': 'Loading screenshot...',
  'admin.verifications.view': 'View',
  'admin.verifications.rejectTitle': 'Reject Verification',
  'admin.verifications.rejectionReason': 'Rejection Reason',
  'admin.verifications.unverify': 'Unverify',
  'admin.verifications.unverifyTitle': 'Unverify exchange',
  'admin.verifications.unverifyBody': 'Move this exchange verification back to Pending. The user access level will not be changed.',
  'admin.verifications.screenshotTitle': 'Verification screenshot',
  'admin.verifications.screenshotFor': '{{exchange}} verification for {{name}}',
  'admin.verifications.screenshotAlt': 'Verification screenshot',
  'admin.verifications.zoomOut': 'Out',
  'admin.verifications.zoomIn': 'In',
  'admin.verifications.zoomAria': 'Screenshot zoom',

  'admin.announcements.title': 'Announcements',
  'admin.announcements.add': 'Add announcement',
  'admin.announcements.loadError': 'Failed to load announcements',
  'admin.announcements.empty': 'No announcements yet',
  'admin.announcements.created': 'Created: {{date}}',
  'admin.announcements.createdBy': 'Created: {{date}} by @{{username}}',
  'admin.announcements.editTitle': 'Edit Announcement',
  'admin.announcements.createTitle': 'Create Announcement',
  'admin.announcements.fieldTitle': 'Title',
  'admin.announcements.content': 'Content',

  'admin.botPosts.title': 'Bot posts',
  'admin.botPosts.detail': 'Compose rich Telegram posts and broadcast them through the bot.',
  'admin.botPosts.broadcastPosts': 'Broadcast posts',
  'admin.botPosts.userMessages': 'User messages',
  'admin.botPosts.composer': 'Composer',
  'admin.botPosts.history': 'History',
  'admin.botPosts.loadError': 'Failed to load bot posts',
  'admin.botPosts.empty': 'No bot posts yet',
  'admin.botPosts.newPost': 'New post',
  'admin.botPosts.titleField': 'Internal title',
  'admin.botPosts.message': 'Telegram message',
  'admin.botPosts.preview': 'Preview',
  'admin.botPosts.uploadImage': 'Upload image',
  'admin.botPosts.dragImage': 'Drop image here or click to upload',
  'admin.botPosts.removeImage': 'Remove image',
  'admin.botPosts.crop': 'Crop',
  'admin.botPosts.cropSquare': 'Square',
  'admin.botPosts.cropPortrait': 'Portrait',
  'admin.botPosts.cropWide': 'Wide',
  'admin.botPosts.cropOriginal': 'Original',
  'admin.botPosts.targetAudience': 'Audience',
  'admin.botPosts.targetAll': 'All users',
  'admin.botPosts.targetBasic': 'Basic users',
  'admin.botPosts.targetPrivate': 'Private users',
  'admin.botPosts.targetUser': 'Selected user',
  'admin.botPosts.userPicker': 'User',
  'admin.botPosts.userPickerPlaceholder': 'Search username, name, or Telegram ID',
  'admin.botPosts.userRequired': 'Pick a user before sending.',
  'admin.botPosts.includeButton': 'Open scanner button',
  'admin.botPosts.defaultButtonText': 'Open scanner',
  'admin.botPosts.buttonText': 'Button text',
  'admin.botPosts.buttonUrl': 'Button URL',
  'admin.botPosts.silent': 'Silent send',
  'admin.botPosts.protectContent': 'Protect from forwarding',
  'admin.botPosts.mediaSpoiler': 'Image spoiler',
  'admin.botPosts.captionAbove': 'Caption above image',
  'admin.botPosts.saveDraft': 'Save draft',
  'admin.botPosts.updateDraft': 'Update draft',
  'admin.botPosts.sendNow': 'Send now',
  'admin.botPosts.sending': 'Sending...',
  'admin.botPosts.saved': 'Draft saved',
  'admin.botPosts.actionFailed': 'Bot post action failed',
  'admin.botPosts.imageUploadFailed': 'Image upload failed',
  'admin.botPosts.imageProcessingFailed': 'Image processing failed',
  'admin.botPosts.confirmSend': 'Send this post to {{audience}}?',
  'admin.botPosts.confirmSendDraft': 'Send "{{title}}" to {{audience}}?',
  'admin.botPosts.confirmDeleteMessages': 'Delete delivered Telegram messages for "{{title}}"?',
  'admin.botPosts.copyTitle': 'Copy of {{title}}',
  'admin.botPosts.statusDraft': 'Draft',
  'admin.botPosts.statusSending': 'Sending',
  'admin.botPosts.statusSent': 'Sent',
  'admin.botPosts.statusFailed': 'Failed',
  'admin.botPosts.statusDeleted': 'Deleted',
  'admin.botPosts.deliveries': '{{sent}} sent / {{failed}} failed / {{skipped}} skipped',
  'admin.botPosts.deletions': '{{deleted}} deleted / {{failed}} delete failed',
  'admin.botPosts.created': 'Created: {{date}}',
  'admin.botPosts.sentAt': 'Sent: {{date}}',
  'admin.botPosts.useDraft': 'Use',
  'admin.botPosts.sendDraft': 'Send draft',
  'admin.botPosts.deleteMessages': 'Delete messages',
  'admin.botPosts.deletingMessages': 'Deleting...',
  'admin.botPosts.imageChip': 'Image',
  'admin.botPosts.botPostAlt': 'Bot post',
  'admin.botPosts.sentSummary': 'Sent {{sent}} of {{total}}. Failed: {{failed}}. Skipped: {{skipped}}.',
  'admin.botPosts.deleteSummary': 'Deleted {{deleted}} of {{total}}. Failed: {{failed}}. Skipped: {{skipped}}.',
  'admin.botPosts.characters': '{{count}} / {{limit}} chars',
  'admin.botPosts.captionLimit': 'Photo captions are limited to 1024 Telegram characters after formatting.',
  'admin.botPosts.formatBold': 'Bold',
  'admin.botPosts.formatItalic': 'Italic',
  'admin.botPosts.formatUnderline': 'Underline',
  'admin.botPosts.formatStrike': 'Strikethrough',
  'admin.botPosts.formatCode': 'Code',
  'admin.botPosts.formatQuote': 'Quote',
  'admin.botPosts.formatSpoiler': 'Spoiler',
  'admin.botPosts.formatLink': 'Link',

  'admin.users.title': 'Users',
  'admin.users.search': 'Search by username, name, ID, or note...',
  'admin.users.user': 'User',
  'admin.users.notes': 'Notes',
  'admin.users.rounds': 'Rounds',
  'admin.users.walletDeposits': 'Deposits',
  'admin.users.viewInfo': 'View full user info',
  'admin.users.editAccessNotes': 'Edit access and notes',
  'admin.users.configureTransferDetails': 'Configure pair transfer details',
  'admin.users.userInfoTitle': 'User Information',
  'admin.users.noUsername': 'No username',
  'admin.users.onboarded': 'Onboarded',
  'admin.users.notOnboarded': 'Not onboarded',
  'admin.users.activity': 'Activity',
  'admin.users.firstActivity': 'First activity',
  'admin.users.lastActivity': 'Last activity',
  'admin.users.lastSeen': 'Last seen',
  'admin.users.profileUpdated': 'Profile updated',
  'admin.users.lastRound': 'Last round',
  'admin.users.lastExchangeSubmission': 'Last exchange submission',
  'admin.users.lastExchangeDecision': 'Last exchange decision',
  'admin.users.lastPairNote': 'Last pair note',
  'admin.users.lastAnnouncementRead': 'Last announcement read',
  'admin.users.lastTransferUpdate': 'Last transfer detail update',
  'admin.users.lastWalletCreated': 'Last wallet created',
  'admin.users.lastWalletDeposit': 'Last wallet deposit',
  'admin.users.profileAndStats': 'Profile and stats',
  'admin.users.profile': 'Profile',
  'admin.users.stats': 'Stats',
  'admin.users.telegramId': 'Telegram ID',
  'admin.users.username': 'Username',
  'admin.users.firstName': 'First name',
  'admin.users.lastName': 'Last name',
  'admin.users.selfReportedBudget': 'Self-reported budget',
  'admin.users.monthlyProfitGoal': 'Monthly profit goal',
  'admin.users.adminNotes': 'Admin notes',
  'admin.users.totalProfit': 'Total profit',
  'admin.users.profit30d': '30d profit',
  'admin.users.avgProfit': 'Avg profit',
  'admin.users.totalExchanges': 'Total exchanges',
  'admin.users.verifiedExchanges': 'Verified exchanges',
  'admin.users.pendingExchanges': 'Pending exchanges',
  'admin.users.rejectedExchanges': 'Rejected exchanges',
  'admin.users.transferDetailsCount': 'Transfer details',
  'admin.users.walletsCount': 'Wallets',
  'admin.users.walletDepositsCount': 'Wallet deposits',
  'admin.users.pairNotesCount': 'Pair notes',
  'admin.users.readAnnouncements': 'Read announcements',
  'admin.users.exchangeVerifications': 'Exchange Verifications',
  'admin.users.noExchangeVerifications': 'No exchange verifications submitted.',
  'admin.users.recentRounds': 'Recent Rounds',
  'admin.users.noRounds': 'No rounds logged.',
  'admin.users.wallets': 'Wallets',
  'admin.users.noWallets': 'No wallets created.',
  'admin.users.walletTransactionCount': '{{count}} tx',
  'admin.users.walletReceived': 'Received: {{amount}}',
  'admin.users.noWalletTransactions': 'No wallet deposits reported.',
  'admin.users.viewAllWalletTransactions': 'View all',
  'admin.users.allWalletTransactionsTitle': 'Wallet deposits - {{wallet}}',
  'admin.users.walletTransactionRange': '{{from}}-{{to}} of {{total}}',
  'admin.users.previousPage': 'Previous',
  'admin.users.nextPage': 'Next',
  'admin.users.walletTransactionSignal': 'Signal #{{id}}',
  'admin.users.walletTransactionFrom': 'From: {{from}}',
  'admin.users.walletTransactionTx': 'TX: {{tx}}',
  'admin.users.transferDetails': 'Transfer Details',
  'admin.users.noTransferDetailsConfigured': 'No transfer details configured.',
  'admin.users.pairNotes': 'Pair Notes',
  'admin.users.noPairNotes': 'No personal pair notes.',
  'admin.users.networkAddress': 'Network: {{network}} | Address: {{address}}',
  'admin.users.memo': 'Memo: {{memo}}',
  'admin.users.updated': 'Updated: {{date}}',
  'admin.users.failedToLoadDetails': 'Failed to load user details',
  'admin.users.editUser': 'Edit User',
  'admin.users.accessLevel': 'Access Level',
  'admin.users.notesPlaceholder': 'Short internal note about this user',
  'admin.users.transferDialogTitle': 'Transfer Details - {{name}}',
  'admin.users.addPairDetails': 'Add Pair Details',
  'admin.users.noPairTransferDetails': 'No pair transfer details set for this user',
  'admin.users.pair': 'Pair',
  'admin.users.address': 'Address',
  'admin.users.depositAddress': 'Deposit Address',
  'admin.users.memoLabel': 'Memo',
  'admin.users.memoTag': 'Memo/Tag',
  'admin.users.editDetails': 'Edit details',
  'admin.users.deleteDetails': 'Delete details',
  'admin.users.editTransferTitle': 'Edit Pair Transfer Details',
  'admin.users.addTransferTitle': 'Add Pair Transfer Details',
  'admin.users.userContextVerifiedPending': 'Verified and pending exchange UIDs',
  'admin.users.loadingExchangeUids': 'Loading exchange UIDs...',
  'admin.users.noActionableExchangeUids': 'No verified or pending exchange UIDs submitted.',
  'admin.users.verifiedCount': '{{count}} verified',
  'admin.users.pendingCount': '{{count}} pending',
  'admin.users.destinationHint': 'Destination is {{exchange}} for {{coin}}. Users will see this only on that exact pair.',
  'admin.users.existingWalletFlow': 'Use existing wallet',
  'admin.users.createWalletFlow': 'Create {{network}} wallet',
  'admin.users.importWalletFlow': 'Import {{network}} wallet',
  'admin.users.existingWalletHint': 'Save an address, memo, or network that already exists. Use this flow for any pair.',
  'admin.users.importWalletHint':
    'Register an existing {{network}} account with the forwarder and save it as this pair deposit address. Active key is required; master/owner key is optional.',
  'admin.users.importAccountName': '{{network}} account name',
  'admin.users.importAccountNameHelp': 'Use an existing on-chain account name. Use 3-16 lowercase letters, digits, or hyphens; start with a letter.',
  'admin.users.importActivePrivateKey': 'Active private key',
  'admin.users.importActivePrivateKeyHelp': 'Required for forwarding, transaction counts, and cooldown tracking. Stored encrypted by the forwarder.',
  'admin.users.importMasterPrivateKey': 'Master/owner private key',
  'admin.users.importMasterPrivateKeyHelp': 'Optional. If supplied, it must match the account owner authority and is stored encrypted by the forwarder.',
  'admin.users.createSteemWallet': 'Create STEEM wallet',
  'admin.users.creatingSteemWallet': 'Creating...',
  'admin.users.importSteemWallet': 'Import STEEM wallet',
  'admin.users.importingSteemWallet': 'Importing...',
  'admin.users.createSteemWalletHint':
    "Enter a STEEM account name, create it through the forwarder API, and save it as this pair's deposit address.",
  'admin.users.createSteemWalletError': 'Failed to create STEEM wallet',
  'admin.users.importSteemWalletError': 'Failed to import STEEM wallet',
  'admin.users.steemAccountName': 'STEEM account name',
  'admin.users.steemAccountNamePlaceholder': 'mxchive-user1',
  'admin.users.steemAccountNameHelp': 'Required for STEEM wallet creation. Use 3-16 lowercase letters, digits, or hyphens; start with a letter.',
  'admin.users.createHiveWallet': 'Create HIVE wallet',
  'admin.users.creatingHiveWallet': 'Creating...',
  'admin.users.importHiveWallet': 'Import HIVE wallet',
  'admin.users.importingHiveWallet': 'Importing...',
  'admin.users.createHiveWalletHint':
    "Enter a HIVE account name, create it through the forwarder API, and save it as this pair's deposit address.",
  'admin.users.createHiveWalletError': 'Failed to create HIVE wallet',
  'admin.users.importHiveWalletError': 'Failed to import HIVE wallet',
  'admin.users.hiveAccountName': 'HIVE account name',
  'admin.users.hiveAccountNamePlaceholder': 'mxhive-user1',
  'admin.users.hiveAccountNameHelp': 'Required for HIVE wallet creation. Use 3-16 lowercase letters, digits, or hyphens; start with a letter.',
  'admin.users.networkPlaceholder': 'HIVE, TRC20, BEP20...',
  'admin.users.addressPlaceholder': 'mxchive',
  'admin.users.memoPlaceholder': '9182911',
} as const;

export type AdminTranslationKey = keyof typeof adminEn;
type AdminTranslationCatalog = Record<AdminTranslationKey, string>;

export const adminCs = {
  'admin.common.access': 'Přístup',
  'admin.common.actions': 'Akce',
  'admin.common.active': 'Aktivní',
  'admin.common.add': 'Přidat',
  'admin.common.all': 'Vše',
  'admin.common.approve': 'Schválit',
  'admin.common.basic': 'Basic',
  'admin.common.cancel': 'Zrušit',
  'admin.common.close': 'Zavřít',
  'admin.common.configured': 'nastaveno',
  'admin.common.create': 'Vytvořit',
  'admin.common.delete': 'Smazat',
  'admin.common.destination': 'Cíl',
  'admin.common.edit': 'Upravit',
  'admin.common.exchange': 'Burza',
  'admin.common.exchanges': 'Burzy',
  'admin.common.inactive': 'Neaktivní',
  'admin.common.loading': 'Načítání...',
  'admin.common.network': 'Síť',
  'admin.common.noNote': 'Bez poznámky',
  'admin.common.paused': 'Pozastaveno',
  'admin.common.pending': 'Čeká',
  'admin.common.private': 'Private',
  'admin.common.rejected': 'Zamítnuto',
  'admin.common.reject': 'Zamítnout',
  'admin.common.save': 'Uložit',
  'admin.common.status': 'Stav',
  'admin.common.total': '{{count}} celkem',
  'admin.common.uid': 'UID',
  'admin.common.update': 'Uložit změny',
  'admin.common.verified': 'Ověřeno',

  'admin.language.current': 'Jazyk adminu',
  'admin.language.openSelector': 'Změnit jazyk adminu',
  'admin.language.title': 'Jazyk adminu',

  'admin.login.title': 'Přihlášení admina',
  'admin.login.subtitle': 'Pro pokračování zadejte přihlašovací údaje.',
  'admin.login.username': 'Uživatelské jméno',
  'admin.login.password': 'Heslo',
  'admin.login.login': 'Přihlásit se',
  'admin.login.loggingIn': 'Přihlašování...',
  'admin.login.invalidCredentials': 'Neplatné přihlašovací údaje',

  'admin.page.eyebrow': 'Admin',
  'admin.page.title': 'Control Panel',
  'admin.page.metricUsers': 'Uživatelé',
  'admin.page.metricPending': 'Čeká',
  'admin.page.metricActivePairs': 'Aktivní páry',
  'admin.page.tabPairs': 'Páry',
  'admin.page.tabVerifications': 'Verifikace',
  'admin.page.tabUsers': 'Uživatelé',
  'admin.page.tabAnnouncements': 'Oznámení',
  'admin.page.tabOperations': 'Provoz',
  'admin.page.tabSignals': 'Signály',
  'admin.page.tabBotPosts': 'Příspěvky botu',

  'admin.time.label': 'Čas',

  'admin.operations.title': 'Provoz',
  'admin.operations.detail': 'Stav wallet API a historie audit logu.',
  'admin.operations.refresh': 'Obnovit',
  'admin.operations.walletApis': 'Wallet API',
  'admin.operations.checkedAt': 'Kontrola: {{date}}',
  'admin.operations.healthLoadError': 'Nepodařilo se načíst stav wallet API',
  'admin.operations.statusOk': 'OK',
  'admin.operations.statusIssue': 'Problém',
  'admin.operations.configured': 'Nastaveno',
  'admin.operations.reachable': 'Dostupné',
  'admin.operations.authorized': 'Autorizováno',
  'admin.operations.auditLog': 'Audit log',
  'admin.operations.auditLoadError': 'Nepodařilo se načíst audit log',
  'admin.operations.noAuditLogs': 'Zatím žádné audit záznamy',
  'admin.operations.time': 'Čas',
  'admin.operations.admin': 'Admin',
  'admin.operations.action': 'Akce',
  'admin.operations.entity': 'Entita',
  'admin.operations.change': 'Změna',

  'admin.pairs.title': 'Správa párů',
  'admin.pairs.detail': '{{configured}} nastaveno - {{active}} aktivní - {{inactive}} neaktivní',
  'admin.pairs.addPair': 'Přidat pár',
  'admin.pairs.position': 'Pozice',
  'admin.pairs.coins': 'Coiny',
  'admin.pairs.spread': 'Spread',
  'admin.pairs.safety': 'Ziskovost',
  'admin.pairs.note': 'Poznámka',
  'admin.pairs.moveGroup': 'Přesunout {{coin}}',
  'admin.pairs.moveUp': 'Přesunout {{coin}} nahoru',
  'admin.pairs.moveDown': 'Přesunout {{coin}} dolů',
  'admin.pairs.editPair': 'Upravit pár',
  'admin.pairs.createPair': 'Vytvořit nový pár',
  'admin.pairs.pairType': 'Typ páru',
  'admin.pairs.singleCoin': 'Jeden coin',
  'admin.pairs.doubleCoin': 'Dva coiny',
  'admin.pairs.coinFrom': 'Coin z',
  'admin.pairs.coinTo': 'Coin do',
  'admin.pairs.exchangeBuy': 'Burza pro nákup',
  'admin.pairs.exchangeSell': 'Burza pro prodej',
  'admin.pairs.accessLevel': 'Úroveň přístupu',
  'admin.pairs.basicAccess': 'Basic (všichni uživatelé)',
  'admin.pairs.privateAccess': 'Private (jen ověření)',
  'admin.pairs.safetySignal': 'Signál ziskovosti',
  'admin.pairs.safetyValue': 'Ziskovost {{percent}} %',
  'admin.pairs.safetyColor': 'Barva ziskovosti',
  'admin.pairs.safe': 'Vysoká',
  'admin.pairs.watch': 'Střední',
  'admin.pairs.risky': 'Nízká',
  'admin.pairs.safetyPercent': 'Ziskovost %',
  'admin.pairs.ribbonColor': 'Barva ribbonu',
  'admin.pairs.pairNote': 'Poznámka k páru',
  'admin.pairs.pairNoteHelp': 'Zobrazí se uživatelům na kartě páru.',
  'admin.pairs.alertMessage': 'Zpráva Telegram alertu',
  'admin.pairs.alertMessageHelp': 'Volitelné. Podporuje {pair}, {route}, {spread}, {coin_from}, {coin_to}, {exchange_buy}, {exchange_sell}.',
  'admin.pairs.baseSpread': 'Základní spread %',
  'admin.pairs.negativeSpread': 'Cooldown spread %',
  'admin.pairs.negativeSpreadShort': 'Cooldown',

  'admin.circles.title': 'Signální kola',
  'admin.circles.detail': '{{active}} aktivní - {{total}} celkem',
  'admin.circles.loadError': 'Signální kola se nepodařilo načíst',
  'admin.circles.launchError': 'Signál se nepodařilo spustit',
  'admin.circles.manualTab': 'Ruční signály',
  'admin.circles.warmupTab': 'Warmup signály',
  'admin.circles.pair': 'Pár',
  'admin.circles.signalSpread': 'Spread signálu %',
  'admin.circles.negativeSpread': 'Cooldown spread %',
  'admin.circles.cooldownHours': 'Cooldown hodiny',
  'admin.circles.expiresIn': 'Vyprší za hodin',
  'admin.circles.notes': 'Poznámky',
  'admin.circles.noPairSelected': 'Není vybraný pár',
  'admin.circles.launch': 'Spustit signál',
  'admin.circles.signalSpreadShort': 'Signál',
  'admin.circles.cooldownShort': 'Cooldown',
  'admin.circles.notifications': 'Notifikace / tx',
  'admin.circles.started': 'Spuštěno',
  'admin.circles.expires': 'Vyprší',
  'admin.circles.stop': 'Zastavit',
  'admin.circles.empty': 'Zatím žádná signální kola',

  'admin.warmups.loadError': 'Warmup plány se nepodařilo načíst',
  'admin.warmups.saveError': 'Warmup plán se nepodařilo uložit',
  'admin.warmups.deleteConfirm': 'Smazat warmup plán "{{name}}"?',
  'admin.warmups.sourceLabel': 'Warmup',
  'admin.warmups.name': 'Název plánu',
  'admin.warmups.pairs': 'Páry',
  'admin.warmups.selectedPairs': '{{count}} vybráno',
  'admin.warmups.noPairsSelected': 'Nejsou vybrané páry',
  'admin.warmups.manualOnlyNotice': 'Každé spuštění náhodně vybere jeden vybraný aktivní pár; interval, délka i spread se také losují z rozsahu. Cron kontroluje každých 7 minut, takže warmup může startovat až asi o 7 minut později. Páry HIVE a STEEM jsou pouze ruční a jsou skryté.',
  'admin.warmups.intervalMin': 'Interval min, min',
  'admin.warmups.intervalMax': 'Interval max, min',
  'admin.warmups.durationMin': 'Délka min, min',
  'admin.warmups.durationMax': 'Délka max, min',
  'admin.warmups.spreadMin': 'Spread min %',
  'admin.warmups.spreadMax': 'Spread max %',
  'admin.warmups.negativeSpread': 'Cooldown spread %',
  'admin.warmups.cooldownHours': 'Cooldown hodiny',
  'admin.warmups.cancelEdit': 'Zrušit úpravu',
  'admin.warmups.create': 'Vytvořit plán',
  'admin.warmups.update': 'Uložit plán',
  'admin.warmups.schedule': 'Plán',
  'admin.warmups.nextRun': 'Další spuštění',
  'admin.warmups.lastRun': 'Poslední spuštění',
  'admin.warmups.runCount': 'Spuštění',
  'admin.warmups.empty': 'Zatím žádné warmup plány',
  'admin.warmups.recentRuns': 'Poslední warmup spuštění',
  'admin.warmups.rule': 'Pravidlo',
  'admin.warmups.duration': 'Délka',
  'admin.warmups.noRuns': 'Zatím žádná warmup spuštění',
  'admin.warmups.runNow': 'Spustit teď',
  'admin.warmups.pause': 'Pozastavit',
  'admin.warmups.resume': 'Obnovit',

  'admin.verifications.title': 'Verifikace',
  'admin.verifications.detail': '{{count}} {{status}}',
  'admin.verifications.loadError': 'Verifikace se nepodařilo načíst',
  'admin.verifications.empty': 'Žádné verifikace ve stavu {{status}}',
  'admin.verifications.telegramId': 'Telegram ID: {{id}}',
  'admin.verifications.exchange': 'Burza: {{exchange}}',
  'admin.verifications.uid': 'UID: {{uid}}',
  'admin.verifications.rejection': 'Důvod zamítnutí: {{reason}}',
  'admin.verifications.screenshot': 'Screenshot:',
  'admin.verifications.revealScreenshot': 'Zobrazit screenshot',
  'admin.verifications.loadingScreenshot': 'Načítání screenshotu...',
  'admin.verifications.view': 'Zobrazit',
  'admin.verifications.rejectTitle': 'Zamítnout verifikaci',
  'admin.verifications.rejectionReason': 'Důvod zamítnutí',
  'admin.verifications.unverify': 'Zrušit ověření',
  'admin.verifications.unverifyTitle': 'Zrušit ověření burzy',
  'admin.verifications.unverifyBody': 'Vrátí toto ověření burzy zpět do stavu Čeká. Úroveň přístupu uživatele se nezmění.',
  'admin.verifications.screenshotTitle': 'Screenshot verifikace',
  'admin.verifications.screenshotFor': 'Verifikace {{exchange}} pro {{name}}',
  'admin.verifications.screenshotAlt': 'Screenshot verifikace',
  'admin.verifications.zoomOut': 'Oddálit',
  'admin.verifications.zoomIn': 'Přiblížit',
  'admin.verifications.zoomAria': 'Zoom screenshotu',

  'admin.announcements.title': 'Oznámení',
  'admin.announcements.add': 'Přidat oznámení',
  'admin.announcements.loadError': 'Oznámení se nepodařilo načíst',
  'admin.announcements.empty': 'Zatím žádná oznámení',
  'admin.announcements.created': 'Vytvořeno: {{date}}',
  'admin.announcements.createdBy': 'Vytvořeno: {{date}} uživatelem @{{username}}',
  'admin.announcements.editTitle': 'Upravit oznámení',
  'admin.announcements.createTitle': 'Vytvořit oznámení',
  'admin.announcements.fieldTitle': 'Název',
  'admin.announcements.content': 'Obsah',

  'admin.botPosts.title': 'Příspěvky botu',
  'admin.botPosts.detail': 'Sestavte bohatě formátované Telegram příspěvky a rozešlete je přes bota.',
  'admin.botPosts.broadcastPosts': 'Hromadné příspěvky',
  'admin.botPosts.userMessages': 'Zprávy uživatelům',
  'admin.botPosts.composer': 'Editor',
  'admin.botPosts.history': 'Historie',
  'admin.botPosts.loadError': 'Příspěvky botu se nepodařilo načíst',
  'admin.botPosts.empty': 'Zatím nejsou žádné příspěvky botu',
  'admin.botPosts.newPost': 'Nový příspěvek',
  'admin.botPosts.titleField': 'Interní název',
  'admin.botPosts.message': 'Telegram zpráva',
  'admin.botPosts.preview': 'Náhled',
  'admin.botPosts.uploadImage': 'Nahrát obrázek',
  'admin.botPosts.dragImage': 'Přetáhněte obrázek sem nebo klikněte pro nahrání',
  'admin.botPosts.removeImage': 'Odebrat obrázek',
  'admin.botPosts.crop': 'Ořez',
  'admin.botPosts.cropSquare': 'Čtverec',
  'admin.botPosts.cropPortrait': 'Portrét',
  'admin.botPosts.cropWide': 'Široký',
  'admin.botPosts.cropOriginal': 'Původní',
  'admin.botPosts.targetAudience': 'Publikum',
  'admin.botPosts.targetAll': 'Všichni uživatelé',
  'admin.botPosts.targetBasic': 'Basic uživatelé',
  'admin.botPosts.targetPrivate': 'Private uživatelé',
  'admin.botPosts.targetUser': 'Vybraný uživatel',
  'admin.botPosts.userPicker': 'Uživatel',
  'admin.botPosts.userPickerPlaceholder': 'Hledat podle username, jména nebo Telegram ID',
  'admin.botPosts.userRequired': 'Před odesláním vyberte uživatele.',
  'admin.botPosts.includeButton': 'Tlačítko Otevřít scanner',
  'admin.botPosts.defaultButtonText': 'Open scanner',
  'admin.botPosts.buttonText': 'Text tlačítka',
  'admin.botPosts.buttonUrl': 'URL tlačítka',
  'admin.botPosts.silent': 'Odeslat tiše',
  'admin.botPosts.protectContent': 'Chránit před přeposláním',
  'admin.botPosts.mediaSpoiler': 'Spoiler obrázku',
  'admin.botPosts.captionAbove': 'Popisek nad obrázkem',
  'admin.botPosts.saveDraft': 'Uložit koncept',
  'admin.botPosts.updateDraft': 'Aktualizovat koncept',
  'admin.botPosts.sendNow': 'Odeslat hned',
  'admin.botPosts.sending': 'Odesílání...',
  'admin.botPosts.saved': 'Koncept uložen',
  'admin.botPosts.actionFailed': 'Akce s příspěvkem botu selhala',
  'admin.botPosts.imageUploadFailed': 'Nahrání obrázku selhalo',
  'admin.botPosts.imageProcessingFailed': 'Zpracování obrázku selhalo',
  'admin.botPosts.confirmSend': 'Odeslat tento příspěvek publiku {{audience}}?',
  'admin.botPosts.confirmSendDraft': 'Odeslat "{{title}}" příjemci {{audience}}?',
  'admin.botPosts.confirmDeleteMessages': 'Smazat doručené Telegram zprávy pro "{{title}}"?',
  'admin.botPosts.copyTitle': 'Kopie: {{title}}',
  'admin.botPosts.statusDraft': 'Koncept',
  'admin.botPosts.statusSending': 'Odesílá se',
  'admin.botPosts.statusSent': 'Odesláno',
  'admin.botPosts.statusFailed': 'Selhalo',
  'admin.botPosts.statusDeleted': 'Smazáno',
  'admin.botPosts.deliveries': '{{sent}} odesláno / {{failed}} selhalo / {{skipped}} přeskočeno',
  'admin.botPosts.deletions': '{{deleted}} smazáno / {{failed}} mazání selhalo',
  'admin.botPosts.created': 'Vytvořeno: {{date}}',
  'admin.botPosts.sentAt': 'Odesláno: {{date}}',
  'admin.botPosts.useDraft': 'Použít',
  'admin.botPosts.sendDraft': 'Odeslat koncept',
  'admin.botPosts.deleteMessages': 'Smazat zprávy',
  'admin.botPosts.deletingMessages': 'Mazání...',
  'admin.botPosts.imageChip': 'Obrázek',
  'admin.botPosts.botPostAlt': 'Příspěvek botu',
  'admin.botPosts.sentSummary': 'Odesláno {{sent}} z {{total}}. Selhalo: {{failed}}. Přeskočeno: {{skipped}}.',
  'admin.botPosts.deleteSummary': 'Smazáno {{deleted}} z {{total}}. Selhalo: {{failed}}. Přeskočeno: {{skipped}}.',
  'admin.botPosts.characters': '{{count}} / {{limit}} znaků',
  'admin.botPosts.captionLimit': 'Popisky fotek jsou po formátování omezené na 1024 znaků Telegramu.',
  'admin.botPosts.formatBold': 'Tučné',
  'admin.botPosts.formatItalic': 'Kurzíva',
  'admin.botPosts.formatUnderline': 'Podtržení',
  'admin.botPosts.formatStrike': 'Přeškrtnutí',
  'admin.botPosts.formatCode': 'Kód',
  'admin.botPosts.formatQuote': 'Citace',
  'admin.botPosts.formatSpoiler': 'Spoiler',
  'admin.botPosts.formatLink': 'Odkaz',

  'admin.users.title': 'Uživatelé',
  'admin.users.search': 'Hledat podle username, jména, ID nebo poznámky...',
  'admin.users.user': 'Uživatel',
  'admin.users.notes': 'Poznámky',
  'admin.users.rounds': 'Kola',
  'admin.users.walletDeposits': 'Vklady',
  'admin.users.viewInfo': 'Zobrazit kompletní info o uživateli',
  'admin.users.editAccessNotes': 'Upravit přístup a poznámky',
  'admin.users.configureTransferDetails': 'Nastavit transfer details pro páry',
  'admin.users.userInfoTitle': 'Informace o uživateli',
  'admin.users.noUsername': 'Bez username',
  'admin.users.onboarded': 'Onboarding dokončen',
  'admin.users.notOnboarded': 'Onboarding nedokončen',
  'admin.users.activity': 'Aktivita',
  'admin.users.firstActivity': 'První aktivita',
  'admin.users.lastActivity': 'Poslední aktivita',
  'admin.users.lastSeen': 'Naposledy online',
  'admin.users.profileUpdated': 'Profil upraven',
  'admin.users.lastRound': 'Poslední kolo',
  'admin.users.lastExchangeSubmission': 'Poslední odeslání burzy',
  'admin.users.lastExchangeDecision': 'Poslední rozhodnutí o burze',
  'admin.users.lastPairNote': 'Poslední poznámka k páru',
  'admin.users.lastAnnouncementRead': 'Poslední přečtené oznámení',
  'admin.users.lastTransferUpdate': 'Poslední úprava transfer details',
  'admin.users.lastWalletCreated': 'Poslední vytvořená peněženka',
  'admin.users.lastWalletDeposit': 'Poslední vklad do peněženky',
  'admin.users.profileAndStats': 'Profil a statistiky',
  'admin.users.profile': 'Profil',
  'admin.users.stats': 'Statistiky',
  'admin.users.telegramId': 'Telegram ID',
  'admin.users.username': 'Username',
  'admin.users.firstName': 'Jméno',
  'admin.users.lastName': 'Příjmení',
  'admin.users.selfReportedBudget': 'Uvedený budget',
  'admin.users.monthlyProfitGoal': 'Měsíční cíl zisku',
  'admin.users.adminNotes': 'Poznámky admina',
  'admin.users.totalProfit': 'Celkový zisk',
  'admin.users.profit30d': 'Zisk za 30 dní',
  'admin.users.avgProfit': 'Prům. zisk',
  'admin.users.totalExchanges': 'Burzy celkem',
  'admin.users.verifiedExchanges': 'Ověřené burzy',
  'admin.users.pendingExchanges': 'Čekající burzy',
  'admin.users.rejectedExchanges': 'Zamítnuté burzy',
  'admin.users.transferDetailsCount': 'Transfer details',
  'admin.users.walletsCount': 'Peněženky',
  'admin.users.walletDepositsCount': 'Vklady peněženek',
  'admin.users.pairNotesCount': 'Poznámky k párům',
  'admin.users.readAnnouncements': 'Přečtená oznámení',
  'admin.users.exchangeVerifications': 'Verifikace burz',
  'admin.users.noExchangeVerifications': 'Nejsou odeslané žádné verifikace burz.',
  'admin.users.recentRounds': 'Nedávná kola',
  'admin.users.noRounds': 'Nejsou zapsaná žádná kola.',
  'admin.users.wallets': 'Peněženky',
  'admin.users.noWallets': 'Žádné peněženky nejsou vytvořené.',
  'admin.users.walletTransactionCount': '{{count}} transakcí',
  'admin.users.walletReceived': 'Přijato: {{amount}}',
  'admin.users.noWalletTransactions': 'Nejsou hlášené žádné vklady do peněženek.',
  'admin.users.viewAllWalletTransactions': 'Zobrazit vše',
  'admin.users.allWalletTransactionsTitle': 'Vklady peněženky - {{wallet}}',
  'admin.users.walletTransactionRange': '{{from}}-{{to}} of {{total}}',
  'admin.users.previousPage': 'Předchozí',
  'admin.users.nextPage': 'Další',
  'admin.users.walletTransactionSignal': 'Signál #{{id}}',
  'admin.users.walletTransactionFrom': 'Od: {{from}}',
  'admin.users.walletTransactionTx': 'TX: {{tx}}',
  'admin.users.transferDetails': 'Transfer Details',
  'admin.users.noTransferDetailsConfigured': 'Nejsou nastavené žádné transfer details.',
  'admin.users.pairNotes': 'Poznámky k párům',
  'admin.users.noPairNotes': 'Žádné osobní poznámky k párům.',
  'admin.users.networkAddress': 'Síť: {{network}} | Adresa: {{address}}',
  'admin.users.memo': 'Memo: {{memo}}',
  'admin.users.updated': 'Upraveno: {{date}}',
  'admin.users.failedToLoadDetails': 'Detail uživatele se nepodařilo načíst',
  'admin.users.editUser': 'Upravit uživatele',
  'admin.users.accessLevel': 'Úroveň přístupu',
  'admin.users.notesPlaceholder': 'Krátká interní poznámka k uživateli',
  'admin.users.transferDialogTitle': 'Transfer Details - {{name}}',
  'admin.users.addPairDetails': 'Přidat údaje k páru',
  'admin.users.noPairTransferDetails': 'Pro tohoto uživatele nejsou nastavené transfer details k párům',
  'admin.users.pair': 'Pár',
  'admin.users.address': 'Adresa',
  'admin.users.depositAddress': 'Depozitní adresa',
  'admin.users.memoLabel': 'Memo',
  'admin.users.memoTag': 'Memo/Tag',
  'admin.users.editDetails': 'Upravit údaje',
  'admin.users.deleteDetails': 'Smazat údaje',
  'admin.users.editTransferTitle': 'Upravit Pair Transfer Details',
  'admin.users.addTransferTitle': 'Přidat Pair Transfer Details',
  'admin.users.userContextVerifiedPending': 'Ověřená a čekající UID na burzách',
  'admin.users.loadingExchangeUids': 'Načítání UID z burz...',
  'admin.users.noActionableExchangeUids': 'Uživatel neodeslal žádná ověřená ani čekající UID.',
  'admin.users.verifiedCount': '{{count}} ověřeno',
  'admin.users.pendingCount': '{{count}} čeká',
  'admin.users.destinationHint': 'Cíl je {{exchange}} pro {{coin}}. Uživatelé to uvidí jen u přesně tohoto páru.',
  'admin.users.existingWalletFlow': 'Použít existující peněženku',
  'admin.users.createWalletFlow': 'Vytvořit peněženku {{network}}',
  'admin.users.existingWalletHint': 'Uložte adresu, memo nebo síť, která už existuje. Tento postup lze použít pro jakýkoliv pár.',
  'admin.users.createSteemWallet': 'Vytvořit peněženku STEEM',
  'admin.users.creatingSteemWallet': 'Vytvářím...',
  'admin.users.createSteemWalletHint':
    'Zadejte název STEEM účtu, vytvořte ho přes forwarder API a uložte ho jako depozitní adresu tohoto páru.',
  'admin.users.createSteemWalletError': 'Nepodařilo se vytvořit peněženku STEEM',
  'admin.users.steemAccountName': 'Název STEEM účtu',
  'admin.users.steemAccountNamePlaceholder': 'mxchive-user1',
  'admin.users.steemAccountNameHelp': 'Vyžadováno pro vytvoření peněženky STEEM. Použijte 3-16 malých písmen, číslic nebo pomlček; začněte písmenem.',
  'admin.users.createHiveWallet': 'Vytvořit peněženku HIVE',
  'admin.users.creatingHiveWallet': 'Vytvářím...',
  'admin.users.createHiveWalletHint':
    'Zadejte název HIVE účtu, vytvořte ho přes forwarder API a uložte ho jako depozitní adresu tohoto páru.',
  'admin.users.createHiveWalletError': 'Nepodařilo se vytvořit peněženku HIVE',
  'admin.users.hiveAccountName': 'Název HIVE účtu',
  'admin.users.hiveAccountNamePlaceholder': 'mxhive-user1',
  'admin.users.hiveAccountNameHelp': 'Vyžadováno pro vytvoření peněženky HIVE. Použijte 3-16 malých písmen, číslic nebo pomlček; začněte písmenem.',
  'admin.users.networkPlaceholder': 'HIVE, TRC20, BEP20...',
  'admin.users.addressPlaceholder': 'mxchive',
  'admin.users.memoPlaceholder': '9182911',
  'admin.users.importWalletFlow': 'Importovat peněženku {{network}}',
  'admin.users.importWalletHint':
    'Zaregistrujte existující účet {{network}} u forwarderu a uložte ho jako depozitní adresu tohoto páru. Active key je povinný; master/owner key je volitelný.',
  'admin.users.importAccountName': 'Název účtu {{network}}',
  'admin.users.importAccountNameHelp': 'Použijte existující on-chain název účtu. Použijte 3-16 malých písmen, číslic nebo pomlček; začněte písmenem.',
  'admin.users.importActivePrivateKey': 'Active privátní klíč',
  'admin.users.importActivePrivateKeyHelp': 'Povinný pro forwardování, počty transakcí a sledování cooldownu. Forwarder ho ukládá šifrovaně.',
  'admin.users.importMasterPrivateKey': 'Master/owner privátní klíč',
  'admin.users.importMasterPrivateKeyHelp': 'Volitelné. Pokud ho zadáte, musí odpovídat owner authority účtu a forwarder ho uloží šifrovaně.',
  'admin.users.importSteemWallet': 'Importovat peněženku STEEM',
  'admin.users.importingSteemWallet': 'Importuji...',
  'admin.users.importSteemWalletError': 'Nepodařilo se importovat peněženku STEEM',
  'admin.users.importHiveWallet': 'Importovat peněženku HIVE',
  'admin.users.importingHiveWallet': 'Importuji...',
  'admin.users.importHiveWalletError': 'Nepodařilo se importovat peněženku HIVE',
} satisfies AdminTranslationCatalog;

export const adminRu = {
  'admin.common.access': 'Доступ',
  'admin.common.actions': 'Действия',
  'admin.common.active': 'Активна',
  'admin.common.add': 'Добавить',
  'admin.common.all': 'Все',
  'admin.common.approve': 'Одобрить',
  'admin.common.basic': 'Basic',
  'admin.common.cancel': 'Отмена',
  'admin.common.close': 'Закрыть',
  'admin.common.configured': 'настроено',
  'admin.common.create': 'Создать',
  'admin.common.delete': 'Удалить',
  'admin.common.destination': 'Назначение',
  'admin.common.edit': 'Редактировать',
  'admin.common.exchange': 'Биржа',
  'admin.common.exchanges': 'Биржи',
  'admin.common.inactive': 'Неактивна',
  'admin.common.loading': 'Загрузка...',
  'admin.common.network': 'Сеть',
  'admin.common.noNote': 'Нет заметки',
  'admin.common.paused': 'Пауза',
  'admin.common.pending': 'Ожидает',
  'admin.common.private': 'Private',
  'admin.common.rejected': 'Отклонено',
  'admin.common.reject': 'Отклонить',
  'admin.common.save': 'Сохранить',
  'admin.common.status': 'Статус',
  'admin.common.total': 'Всего: {{count}}',
  'admin.common.uid': 'UID',
  'admin.common.update': 'Обновить',
  'admin.common.verified': 'Проверено',

  'admin.language.current': 'Язык админки',
  'admin.language.openSelector': 'Сменить язык админки',
  'admin.language.title': 'Язык админки',

  'admin.login.title': 'Вход в админку',
  'admin.login.subtitle': 'Введите логин и пароль, чтобы продолжить.',
  'admin.login.username': 'Логин',
  'admin.login.password': 'Пароль',
  'admin.login.login': 'Войти',
  'admin.login.loggingIn': 'Вход...',
  'admin.login.invalidCredentials': 'Неверный логин или пароль',

  'admin.page.eyebrow': 'Admin',
  'admin.page.title': 'Панель управления',
  'admin.page.metricUsers': 'Пользователи',
  'admin.page.metricPending': 'Ожидают',
  'admin.page.metricActivePairs': 'Активные пары',
  'admin.page.tabPairs': 'Пары',
  'admin.page.tabVerifications': 'Проверки',
  'admin.page.tabUsers': 'Пользователи',
  'admin.page.tabAnnouncements': 'Объявления',
  'admin.page.tabOperations': 'Операции',
  'admin.page.tabSignals': 'Сигналы',
  'admin.page.tabBotPosts': 'Посты бота',

  'admin.time.label': 'Время',

  'admin.operations.title': 'Операции',
  'admin.operations.detail': 'Статус API кошельков и история действий администраторов.',
  'admin.operations.refresh': 'Обновить',
  'admin.operations.walletApis': 'API кошельков',
  'admin.operations.checkedAt': 'Проверено: {{date}}',
  'admin.operations.healthLoadError': 'Не удалось загрузить статус API кошельков',
  'admin.operations.statusOk': 'OK',
  'admin.operations.statusIssue': 'Проблема',
  'admin.operations.configured': 'Настроено',
  'admin.operations.reachable': 'Доступно',
  'admin.operations.authorized': 'Авторизовано',
  'admin.operations.auditLog': 'Журнал аудита',
  'admin.operations.auditLoadError': 'Не удалось загрузить журнал аудита',
  'admin.operations.noAuditLogs': 'Записей аудита пока нет',
  'admin.operations.time': 'Время',
  'admin.operations.admin': 'Админ',
  'admin.operations.action': 'Действие',
  'admin.operations.entity': 'Объект',
  'admin.operations.change': 'Изменение',

  'admin.pairs.title': 'Управление парами',
  'admin.pairs.detail': '{{configured}} настроено - {{active}} активных - {{inactive}} неактивных',
  'admin.pairs.addPair': 'Добавить пару',
  'admin.pairs.position': 'Позиция',
  'admin.pairs.coins': 'Монеты',
  'admin.pairs.spread': 'Spread',
  'admin.pairs.safety': 'Доходность',
  'admin.pairs.note': 'Заметка',
  'admin.pairs.moveGroup': 'Переместить {{coin}}',
  'admin.pairs.moveUp': 'Переместить {{coin}} выше',
  'admin.pairs.moveDown': 'Переместить {{coin}} ниже',
  'admin.pairs.editPair': 'Редактировать пару',
  'admin.pairs.createPair': 'Создать новую пару',
  'admin.pairs.pairType': 'Тип пары',
  'admin.pairs.singleCoin': 'Одна монета',
  'admin.pairs.doubleCoin': 'Две монеты',
  'admin.pairs.coinFrom': 'Монета исходная',
  'admin.pairs.coinTo': 'Монета конечная',
  'admin.pairs.exchangeBuy': 'Биржа покупки',
  'admin.pairs.exchangeSell': 'Биржа продажи',
  'admin.pairs.accessLevel': 'Уровень доступа',
  'admin.pairs.basicAccess': 'Basic (все пользователи)',
  'admin.pairs.privateAccess': 'Private (только проверенные)',
  'admin.pairs.safetySignal': 'Сигнал доходности',
  'admin.pairs.safetyValue': 'Доходность {{percent}}%',
  'admin.pairs.safetyColor': 'Цвет доходности',
  'admin.pairs.safe': 'Высокая',
  'admin.pairs.watch': 'Средняя',
  'admin.pairs.risky': 'Низкая',
  'admin.pairs.safetyPercent': 'Доходность %',
  'admin.pairs.ribbonColor': 'Цвет полосы',
  'admin.pairs.pairNote': 'Заметка к паре',
  'admin.pairs.pairNoteHelp': 'Показывается пользователям на карточке пары.',
  'admin.pairs.alertMessage': 'Текст Telegram алерта',
  'admin.pairs.alertMessageHelp': 'Опционально. Поддерживает {pair}, {route}, {spread}, {coin_from}, {coin_to}, {exchange_buy}, {exchange_sell}.',
  'admin.pairs.baseSpread': 'Базовый спред %',
  'admin.pairs.negativeSpread': 'Cooldown спред %',
  'admin.pairs.negativeSpreadShort': 'Cooldown',

  'admin.circles.title': 'Сигнальные круги',
  'admin.circles.detail': '{{active}} активных - {{total}} всего',
  'admin.circles.loadError': 'Не удалось загрузить сигнальные круги',
  'admin.circles.launchError': 'Не удалось запустить сигнал',
  'admin.circles.manualTab': 'Ручные сигналы',
  'admin.circles.warmupTab': 'Warmup сигналы',
  'admin.circles.pair': 'Пара',
  'admin.circles.signalSpread': 'Спред сигнала %',
  'admin.circles.negativeSpread': 'Cooldown спред %',
  'admin.circles.cooldownHours': 'Cooldown, часы',
  'admin.circles.expiresIn': 'Истекает через часов',
  'admin.circles.notes': 'Заметки',
  'admin.circles.noPairSelected': 'Пара не выбрана',
  'admin.circles.launch': 'Запустить сигнал',
  'admin.circles.signalSpreadShort': 'Сигнал',
  'admin.circles.cooldownShort': 'Cooldown',
  'admin.circles.notifications': 'Уведомления / tx',
  'admin.circles.started': 'Запущен',
  'admin.circles.expires': 'Истекает',
  'admin.circles.stop': 'Остановить',
  'admin.circles.empty': 'Сигнальных кругов пока нет',

  'admin.warmups.loadError': 'Не удалось загрузить warmup-расписания',
  'admin.warmups.saveError': 'Не удалось сохранить warmup-расписание',
  'admin.warmups.deleteConfirm': 'Удалить warmup-расписание "{{name}}"?',
  'admin.warmups.sourceLabel': 'Warmup',
  'admin.warmups.name': 'Название расписания',
  'admin.warmups.pairs': 'Пары',
  'admin.warmups.selectedPairs': '{{count}} выбрано',
  'admin.warmups.noPairsSelected': 'Пары не выбраны',
  'admin.warmups.manualOnlyNotice': 'Каждый запуск случайно выбирает одну выбранную активную пару; интервал, длина и спред тоже выбираются из диапазона. Cron проверяет каждые 7 минут, поэтому warmup может стартовать примерно до 7 минут позже. Пары HIVE и STEEM доступны только вручную и скрыты.',
  'admin.warmups.intervalMin': 'Интервал мин, мин',
  'admin.warmups.intervalMax': 'Интервал макс, мин',
  'admin.warmups.durationMin': 'Длина мин, мин',
  'admin.warmups.durationMax': 'Длина макс, мин',
  'admin.warmups.spreadMin': 'Спред мин %',
  'admin.warmups.spreadMax': 'Спред макс %',
  'admin.warmups.negativeSpread': 'Cooldown спред %',
  'admin.warmups.cooldownHours': 'Cooldown, часы',
  'admin.warmups.cancelEdit': 'Отменить правку',
  'admin.warmups.create': 'Создать расписание',
  'admin.warmups.update': 'Сохранить расписание',
  'admin.warmups.schedule': 'Расписание',
  'admin.warmups.nextRun': 'Следующий запуск',
  'admin.warmups.lastRun': 'Последний запуск',
  'admin.warmups.runCount': 'Запуски',
  'admin.warmups.empty': 'Warmup-расписаний пока нет',
  'admin.warmups.recentRuns': 'Последние warmup-запуски',
  'admin.warmups.rule': 'Правило',
  'admin.warmups.duration': 'Длина',
  'admin.warmups.noRuns': 'Warmup-запусков пока нет',
  'admin.warmups.runNow': 'Запустить сейчас',
  'admin.warmups.pause': 'Пауза',
  'admin.warmups.resume': 'Возобновить',

  'admin.verifications.title': 'Проверки',
  'admin.verifications.detail': '{{count}}: {{status}}',
  'admin.verifications.loadError': 'Не удалось загрузить проверки',
  'admin.verifications.empty': 'Нет проверок со статусом "{{status}}"',
  'admin.verifications.telegramId': 'Telegram ID: {{id}}',
  'admin.verifications.exchange': 'Биржа: {{exchange}}',
  'admin.verifications.uid': 'UID: {{uid}}',
  'admin.verifications.rejection': 'Причина отклонения: {{reason}}',
  'admin.verifications.screenshot': 'Скриншот:',
  'admin.verifications.revealScreenshot': 'Показать скриншот',
  'admin.verifications.loadingScreenshot': 'Загрузка скриншота...',
  'admin.verifications.view': 'Открыть',
  'admin.verifications.rejectTitle': 'Отклонить проверку',
  'admin.verifications.rejectionReason': 'Причина отклонения',
  'admin.verifications.unverify': 'Снять проверку',
  'admin.verifications.unverifyTitle': 'Снять проверку биржи',
  'admin.verifications.unverifyBody': 'Переводит эту проверку биржи обратно в статус ожидания. Уровень доступа пользователя не изменится.',
  'admin.verifications.screenshotTitle': 'Скриншот проверки',
  'admin.verifications.screenshotFor': 'Проверка {{exchange}} для {{name}}',
  'admin.verifications.screenshotAlt': 'Скриншот проверки',
  'admin.verifications.zoomOut': 'Отдалить',
  'admin.verifications.zoomIn': 'Приблизить',
  'admin.verifications.zoomAria': 'Масштаб скриншота',

  'admin.announcements.title': 'Объявления',
  'admin.announcements.add': 'Добавить объявление',
  'admin.announcements.loadError': 'Не удалось загрузить объявления',
  'admin.announcements.empty': 'Объявлений пока нет',
  'admin.announcements.created': 'Создано: {{date}}',
  'admin.announcements.createdBy': 'Создано: {{date}} пользователем @{{username}}',
  'admin.announcements.editTitle': 'Редактировать объявление',
  'admin.announcements.createTitle': 'Создать объявление',
  'admin.announcements.fieldTitle': 'Заголовок',
  'admin.announcements.content': 'Текст',

  'admin.botPosts.title': 'Посты бота',
  'admin.botPosts.detail': 'Создавайте форматированные Telegram-посты и рассылайте их через бота.',
  'admin.botPosts.broadcastPosts': 'Рассылки',
  'admin.botPosts.userMessages': 'Сообщения пользователям',
  'admin.botPosts.composer': 'Редактор',
  'admin.botPosts.history': 'История',
  'admin.botPosts.loadError': 'Не удалось загрузить посты бота',
  'admin.botPosts.empty': 'Постов бота пока нет',
  'admin.botPosts.newPost': 'Новый пост',
  'admin.botPosts.titleField': 'Внутренний заголовок',
  'admin.botPosts.message': 'Сообщение Telegram',
  'admin.botPosts.preview': 'Предпросмотр',
  'admin.botPosts.uploadImage': 'Загрузить изображение',
  'admin.botPosts.dragImage': 'Перетащите изображение сюда или нажмите для загрузки',
  'admin.botPosts.removeImage': 'Удалить изображение',
  'admin.botPosts.crop': 'Обрезка',
  'admin.botPosts.cropSquare': 'Квадрат',
  'admin.botPosts.cropPortrait': 'Портрет',
  'admin.botPosts.cropWide': 'Широкий',
  'admin.botPosts.cropOriginal': 'Оригинал',
  'admin.botPosts.targetAudience': 'Аудитория',
  'admin.botPosts.targetAll': 'Все пользователи',
  'admin.botPosts.targetBasic': 'Basic пользователи',
  'admin.botPosts.targetPrivate': 'Private пользователи',
  'admin.botPosts.targetUser': 'Выбранный пользователь',
  'admin.botPosts.userPicker': 'Пользователь',
  'admin.botPosts.userPickerPlaceholder': 'Поиск по username, имени или Telegram ID',
  'admin.botPosts.userRequired': 'Выберите пользователя перед отправкой.',
  'admin.botPosts.includeButton': 'Кнопка Open scanner',
  'admin.botPosts.defaultButtonText': 'Open scanner',
  'admin.botPosts.buttonText': 'Текст кнопки',
  'admin.botPosts.buttonUrl': 'URL кнопки',
  'admin.botPosts.silent': 'Отправить без звука',
  'admin.botPosts.protectContent': 'Запретить пересылку',
  'admin.botPosts.mediaSpoiler': 'Спойлер изображения',
  'admin.botPosts.captionAbove': 'Подпись над изображением',
  'admin.botPosts.saveDraft': 'Сохранить черновик',
  'admin.botPosts.updateDraft': 'Обновить черновик',
  'admin.botPosts.sendNow': 'Отправить сейчас',
  'admin.botPosts.sending': 'Отправка...',
  'admin.botPosts.saved': 'Черновик сохранен',
  'admin.botPosts.actionFailed': 'Действие с постом бота не выполнено',
  'admin.botPosts.imageUploadFailed': 'Не удалось загрузить изображение',
  'admin.botPosts.imageProcessingFailed': 'Не удалось обработать изображение',
  'admin.botPosts.confirmSend': 'Отправить этот пост аудитории {{audience}}?',
  'admin.botPosts.confirmSendDraft': 'Отправить "{{title}}" получателю {{audience}}?',
  'admin.botPosts.confirmDeleteMessages': 'Удалить доставленные сообщения Telegram для "{{title}}"?',
  'admin.botPosts.copyTitle': 'Копия: {{title}}',
  'admin.botPosts.statusDraft': 'Черновик',
  'admin.botPosts.statusSending': 'Отправляется',
  'admin.botPosts.statusSent': 'Отправлен',
  'admin.botPosts.statusFailed': 'Ошибка',
  'admin.botPosts.statusDeleted': 'Удален',
  'admin.botPosts.deliveries': '{{sent}} отправлено / {{failed}} ошибок / {{skipped}} пропущено',
  'admin.botPosts.deletions': '{{deleted}} удалено / {{failed}} ошибок удаления',
  'admin.botPosts.created': 'Создано: {{date}}',
  'admin.botPosts.sentAt': 'Отправлено: {{date}}',
  'admin.botPosts.useDraft': 'Использовать',
  'admin.botPosts.sendDraft': 'Отправить черновик',
  'admin.botPosts.deleteMessages': 'Удалить сообщения',
  'admin.botPosts.deletingMessages': 'Удаление...',
  'admin.botPosts.imageChip': 'Изображение',
  'admin.botPosts.botPostAlt': 'Пост бота',
  'admin.botPosts.sentSummary': 'Отправлено {{sent}} из {{total}}. Ошибок: {{failed}}. Пропущено: {{skipped}}.',
  'admin.botPosts.deleteSummary': 'Удалено {{deleted}} из {{total}}. Ошибок: {{failed}}. Пропущено: {{skipped}}.',
  'admin.botPosts.characters': '{{count}} / {{limit}} символов',
  'admin.botPosts.captionLimit': 'Подписи к фото ограничены 1024 символами Telegram после форматирования.',
  'admin.botPosts.formatBold': 'Жирный',
  'admin.botPosts.formatItalic': 'Курсив',
  'admin.botPosts.formatUnderline': 'Подчеркивание',
  'admin.botPosts.formatStrike': 'Зачеркивание',
  'admin.botPosts.formatCode': 'Код',
  'admin.botPosts.formatQuote': 'Цитата',
  'admin.botPosts.formatSpoiler': 'Спойлер',
  'admin.botPosts.formatLink': 'Ссылка',

  'admin.users.title': 'Пользователи',
  'admin.users.search': 'Поиск по username, имени, ID или заметке...',
  'admin.users.user': 'Пользователь',
  'admin.users.notes': 'Заметки',
  'admin.users.rounds': 'Круги',
  'admin.users.walletDeposits': 'Депозиты',
  'admin.users.viewInfo': 'Показать всю информацию о пользователе',
  'admin.users.editAccessNotes': 'Редактировать доступ и заметки',
  'admin.users.configureTransferDetails': 'Настроить реквизиты для пар',
  'admin.users.userInfoTitle': 'Информация о пользователе',
  'admin.users.noUsername': 'Нет username',
  'admin.users.onboarded': 'Онбординг пройден',
  'admin.users.notOnboarded': 'Онбординг не пройден',
  'admin.users.activity': 'Активность',
  'admin.users.firstActivity': 'Первая активность',
  'admin.users.lastActivity': 'Последняя активность',
  'admin.users.lastSeen': 'Последний визит',
  'admin.users.profileUpdated': 'Профиль обновлен',
  'admin.users.lastRound': 'Последний круг',
  'admin.users.lastExchangeSubmission': 'Последняя заявка по бирже',
  'admin.users.lastExchangeDecision': 'Последнее решение по бирже',
  'admin.users.lastPairNote': 'Последняя заметка к паре',
  'admin.users.lastAnnouncementRead': 'Последнее прочитанное объявление',
  'admin.users.lastTransferUpdate': 'Последнее обновление реквизитов',
  'admin.users.lastWalletCreated': 'Последний созданный кошелек',
  'admin.users.lastWalletDeposit': 'Последний депозит кошелька',
  'admin.users.profileAndStats': 'Профиль и статистика',
  'admin.users.profile': 'Профиль',
  'admin.users.stats': 'Статистика',
  'admin.users.telegramId': 'Telegram ID',
  'admin.users.username': 'Username',
  'admin.users.firstName': 'Имя',
  'admin.users.lastName': 'Фамилия',
  'admin.users.selfReportedBudget': 'Указанный бюджет',
  'admin.users.monthlyProfitGoal': 'Месячная цель по прибыли',
  'admin.users.adminNotes': 'Заметки админа',
  'admin.users.totalProfit': 'Общая прибыль',
  'admin.users.profit30d': 'Прибыль за 30 дней',
  'admin.users.avgProfit': 'Средняя прибыль',
  'admin.users.totalExchanges': 'Биржи всего',
  'admin.users.verifiedExchanges': 'Проверенные биржи',
  'admin.users.pendingExchanges': 'Биржи на проверке',
  'admin.users.rejectedExchanges': 'Отклоненные биржи',
  'admin.users.transferDetailsCount': 'Реквизиты',
  'admin.users.walletsCount': 'Кошельки',
  'admin.users.walletDepositsCount': 'Депозиты кошельков',
  'admin.users.pairNotesCount': 'Заметки к парам',
  'admin.users.readAnnouncements': 'Прочитанные объявления',
  'admin.users.exchangeVerifications': 'Проверки бирж',
  'admin.users.noExchangeVerifications': 'Проверки бирж еще не отправлялись.',
  'admin.users.recentRounds': 'Недавние круги',
  'admin.users.noRounds': 'Круги еще не записывались.',
  'admin.users.wallets': 'Кошельки',
  'admin.users.noWallets': 'Кошельки еще не создавались.',
  'admin.users.walletTransactionCount': '{{count}} транз.',
  'admin.users.walletReceived': 'Получено: {{amount}}',
  'admin.users.noWalletTransactions': 'Нет данных о депозитах кошелька.',
  'admin.users.viewAllWalletTransactions': 'Показать все',
  'admin.users.allWalletTransactionsTitle': 'Депозиты кошелька - {{wallet}}',
  'admin.users.walletTransactionRange': '{{from}}-{{to}} of {{total}}',
  'admin.users.previousPage': 'Назад',
  'admin.users.nextPage': 'Далее',
  'admin.users.walletTransactionSignal': 'Сигнал #{{id}}',
  'admin.users.walletTransactionFrom': 'От: {{from}}',
  'admin.users.walletTransactionTx': 'TX: {{tx}}',
  'admin.users.transferDetails': 'Реквизиты перевода',
  'admin.users.noTransferDetailsConfigured': 'Реквизиты перевода не настроены.',
  'admin.users.pairNotes': 'Заметки к парам',
  'admin.users.noPairNotes': 'Личных заметок к парам нет.',
  'admin.users.networkAddress': 'Сеть: {{network}} | Адрес: {{address}}',
  'admin.users.memo': 'Memo: {{memo}}',
  'admin.users.updated': 'Обновлено: {{date}}',
  'admin.users.failedToLoadDetails': 'Не удалось загрузить данные пользователя',
  'admin.users.editUser': 'Редактировать пользователя',
  'admin.users.accessLevel': 'Уровень доступа',
  'admin.users.notesPlaceholder': 'Короткая внутренняя заметка о пользователе',
  'admin.users.transferDialogTitle': 'Реквизиты перевода - {{name}}',
  'admin.users.addPairDetails': 'Добавить реквизиты пары',
  'admin.users.noPairTransferDetails': 'Для этого пользователя нет реквизитов по парам',
  'admin.users.pair': 'Пара',
  'admin.users.address': 'Адрес',
  'admin.users.depositAddress': 'Адрес депозита',
  'admin.users.memoLabel': 'Memo',
  'admin.users.memoTag': 'Memo/Tag',
  'admin.users.editDetails': 'Редактировать реквизиты',
  'admin.users.deleteDetails': 'Удалить реквизиты',
  'admin.users.editTransferTitle': 'Редактировать реквизиты пары',
  'admin.users.addTransferTitle': 'Добавить реквизиты пары',
  'admin.users.userContextVerifiedPending': 'Проверенные и ожидающие UID на биржах',
  'admin.users.loadingExchangeUids': 'Загрузка UID с бирж...',
  'admin.users.noActionableExchangeUids': 'Пользователь не отправлял проверенные или ожидающие UID.',
  'admin.users.verifiedCount': 'Проверено: {{count}}',
  'admin.users.pendingCount': 'Ожидает: {{count}}',
  'admin.users.destinationHint': 'Назначение: {{exchange}} для {{coin}}. Пользователь увидит это только у этой пары.',
  'admin.users.existingWalletFlow': 'Использовать существующий кошелек',
  'admin.users.createWalletFlow': 'Создать кошелек {{network}}',
  'admin.users.existingWalletHint': 'Сохраните уже существующие адрес, memo или сеть. Этот сценарий подходит для любой пары.',
  'admin.users.createSteemWallet': 'Создать кошелек STEEM',
  'admin.users.creatingSteemWallet': 'Создание...',
  'admin.users.createSteemWalletHint':
    'Введите имя STEEM-аккаунта, создайте его через API форвардера и сохраните как адрес депозита для этой пары.',
  'admin.users.createSteemWalletError': 'Не удалось создать кошелек STEEM',
  'admin.users.steemAccountName': 'Имя STEEM-аккаунта',
  'admin.users.steemAccountNamePlaceholder': 'mxchive-user1',
  'admin.users.steemAccountNameHelp': 'Обязательно для создания кошелька STEEM. Используйте 3-16 строчных латинских букв, цифр или дефисов; первый символ - буква.',
  'admin.users.createHiveWallet': 'Создать кошелек HIVE',
  'admin.users.creatingHiveWallet': 'Создание...',
  'admin.users.createHiveWalletHint':
    'Введите имя HIVE-аккаунта, создайте его через API форвардера и сохраните как адрес депозита для этой пары.',
  'admin.users.createHiveWalletError': 'Не удалось создать кошелек HIVE',
  'admin.users.hiveAccountName': 'Имя HIVE-аккаунта',
  'admin.users.hiveAccountNamePlaceholder': 'mxhive-user1',
  'admin.users.hiveAccountNameHelp': 'Обязательно для создания кошелька HIVE. Используйте 3-16 строчных латинских букв, цифр или дефисов; первый символ - буква.',
  'admin.users.networkPlaceholder': 'HIVE, TRC20, BEP20...',
  'admin.users.addressPlaceholder': 'mxchive',
  'admin.users.memoPlaceholder': '9182911',
  'admin.users.importWalletFlow': 'Импортировать кошелек {{network}}',
  'admin.users.importWalletHint':
    'Зарегистрируйте существующий аккаунт {{network}} в форвардере и сохраните его как адрес депозита для этой пары. Active key обязателен; master/owner key опционален.',
  'admin.users.importAccountName': 'Имя аккаунта {{network}}',
  'admin.users.importAccountNameHelp': 'Используйте существующее on-chain имя аккаунта. Используйте 3-16 строчных латинских букв, цифр или дефисов; первый символ - буква.',
  'admin.users.importActivePrivateKey': 'Active приватный ключ',
  'admin.users.importActivePrivateKeyHelp': 'Обязателен для форвардинга, подсчета транзакций и отслеживания cooldown. Хранится форвардером в зашифрованном виде.',
  'admin.users.importMasterPrivateKey': 'Master/owner приватный ключ',
  'admin.users.importMasterPrivateKeyHelp': 'Необязательно. Если указан, он должен соответствовать owner authority аккаунта и хранится форвардером в зашифрованном виде.',
  'admin.users.importSteemWallet': 'Импортировать кошелек STEEM',
  'admin.users.importingSteemWallet': 'Импорт...',
  'admin.users.importSteemWalletError': 'Не удалось импортировать кошелек STEEM',
  'admin.users.importHiveWallet': 'Импортировать кошелек HIVE',
  'admin.users.importingHiveWallet': 'Импорт...',
  'admin.users.importHiveWalletError': 'Не удалось импортировать кошелек HIVE',
} satisfies AdminTranslationCatalog;

export const ADMIN_TRANSLATIONS: Record<AdminLanguageCode, AdminTranslationCatalog> = {
  en: adminEn,
  cs: adminCs,
  ru: adminRu,
};
